import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QuizRenderer } from '@inso_web/quiz-previewer';
import type { QuizQuestion, QuizSettings } from '@inso_web/quiz-previewer/types';

interface ShareResponse {
  ok: boolean;
  quiz: { id: string; slug: string; titleRu: string; titleEn: string; questions: QuizQuestion[]; settings: QuizSettings };
  permission?: 'VIEW' | 'EDIT';
}
import { quizApi } from '../api/client';

// Защищённый share: quiz.insoweb.ru/share/{token}. Если share с паролем —
// показываем PasswordGate; иначе — рендерим квиз. Пароль хранится в
// sessionStorage и шлётся в X-Share-Password header.

const passwordKey = (token: string) => `quiz-share-pwd-${token}`;

export function SharedQuizPage() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState<string | undefined>(() => {
    if (!token) return undefined;
    return sessionStorage.getItem(passwordKey(token)) ?? undefined;
  });
  const [needsPassword, setNeedsPassword] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['shared-quiz', token, password],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (password) headers['X-Share-Password'] = password;
      try {
        const r = await quizApi.get<ShareResponse>(`/share/${token}`, { headers });
        return r.data.quiz;
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401) {
          setNeedsPassword(true);
        }
        throw err;
      }
    },
    enabled: !!token && (!needsPassword || !!password),
    retry: false,
  });

  if (needsPassword && !data) {
    return <PasswordGate onSubmit={(pwd) => { sessionStorage.setItem(passwordKey(token!), pwd); setPassword(pwd); setNeedsPassword(false); }} />;
  }
  if (isLoading) return <CenteredText>Loading…</CenteredText>;
  if (error || !data) return <CenteredText>Share link is invalid or expired</CenteredText>;

  const handleComplete = async (answers: Record<string, unknown>) => {
    const headers: Record<string, string> = {};
    if (password) headers['X-Share-Password'] = password;
    await quizApi.post(`/share/${token}/response`, { answers, metadata: {}, completedAt: new Date().toISOString() }, { headers });
  };

  return (
    <QuizRenderer
      questions={data.questions ?? []}
      settings={data.settings ?? ({} as QuizSettings)}
      quizId={data.id}
      lang="ru"
      onComplete={handleComplete}
    />
  );
}

function PasswordGate({ onSubmit }: { onSubmit: (pwd: string) => void }) {
  const [pwd, setPwd] = useState('');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (pwd) onSubmit(pwd); }}
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', fontFamily: 'system-ui, sans-serif' }}
    >
      <div style={{ width: 320, padding: 24, border: '1px solid #e5e5e5', borderRadius: 12 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Введите пароль</h2>
        <p style={{ color: '#666', fontSize: 13, margin: '0 0 16px' }}>Этот квиз защищён паролем.</p>
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Пароль"
          autoFocus
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: 8, marginBottom: 12 }}
        />
        <button type="submit" style={{ width: '100%', padding: '10px 16px', background: '#000', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          Открыть
        </button>
      </div>
    </form>
  );
}

function CenteredText({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', fontFamily: 'system-ui, sans-serif', color: '#666' }}>
      <p>{children}</p>
    </div>
  );
}
